import { JsonValue, create } from '@bufbuild/protobuf'
import { Form } from 'antd'
import { useCallback, useMemo, useState } from 'react'

import { useListControls } from '@/api/control.hook'
import {
  isJiraIssueTooLargeError,
  isJiraNoPermissionError,
  useCreateJiraIssue,
  useCreateJiraIssuesBulkWithChunking,
  useGetIssueTypeRequiredFields,
  useGetJiraSettings,
  useListProjectIssueTypes,
  useListProjects,
} from '@/api/jira.hook'
import { ListControlsRequestSchema } from '@/gen/inventory/v1/control_service_pb'
import { CreateIssueRequest, CreateIssueRequestSchema } from '@/gen/jira/v1/jira_service_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { isUnvalidatedControlStatus } from '@/lib/control-status'

import { useErrorModal } from '@/pages/assessment/components/create-jira-issue-modal/use-error-modal'
import { useGenerateJiraTicketContent } from '@/pages/assessment/components/create-jira-issue-modal/use-generate-jira-ticket-content'
import { useJiraPreviewIssueModal } from '@/pages/assessment/components/create-jira-issue-modal/use-jira-preview-issue-modal'
import { AgControlTable } from '@/pages/control/ag-grid-control'

import { Modal } from '@/components/lemmala-design-system/modal/modal'

import { JiraFieldInput } from './jira-field-input'
import { JIRA_CREATE_ISSUE_MODAL_TEXT } from './labels'

type JiraCreateIssueModalProps = {
  companyId: string
  assessmentId: string
  onCancel?: () => void
  onFinish: (response: { id: string; key: string; link: string }) => void
}

export function JiraCreateIssueModal({
  companyId,
  assessmentId,
  onCancel,
  onFinish,
}: JiraCreateIssueModalProps) {
  const trackIssueCreated = useTrackCallback('jira.issue-created')
  const { showModal: showErrorModal, renderModal: renderErrorModal } = useErrorModal()
  const { mutateAsync: createJiraIssue, isPending: isCreatingIssue } = useCreateJiraIssue()
  const { mutateAsync: createJiraIssuesBulkWithChunking } = useCreateJiraIssuesBulkWithChunking()
  const { data: integrationSettings } = useGetJiraSettings()
  const { data: projectsData } = useListProjects({
    enabled: !!integrationSettings?.settings?.defaultProjectKey,
  })
  const { data: issueTypesData, isLoading: isLoadingIssueTypes } = useListProjectIssueTypes({
    projectId:
      projectsData?.projects.find(
        (project) => project.key === integrationSettings?.settings?.defaultProjectKey,
      )?.id ?? '',
    enabled: !!integrationSettings?.settings?.defaultProjectKey,
  })
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([])
  const { data: controlsData, isLoading: isLoadingControls } = useListControls({
    ...create(ListControlsRequestSchema, { assessmentId }),
    enabled: !!assessmentId,
  })
  const { data: taskRequiredFields, isLoading: isLoadingTaskRequiredFields } =
    useGetIssueTypeRequiredFields({
      projectIdOrKey: integrationSettings?.settings?.defaultProjectKey ?? '',
      issueTypeId:
        issueTypesData?.issueTypes.find(
          (type) => type.name === integrationSettings?.settings?.defaultIssueTypeName,
        )?.id ?? '',
      enabled:
        !!issueTypesData &&
        !!integrationSettings?.settings?.defaultProjectKey &&
        !!integrationSettings?.settings?.defaultIssueTypeName,
    })
  const { data: subtaskRequiredFields, isLoading: isLoadingSubtaskRequiredFields } =
    useGetIssueTypeRequiredFields({
      projectIdOrKey: integrationSettings?.settings?.defaultProjectKey ?? '',
      issueTypeId:
        issueTypesData?.issueTypes.find(
          (type) => type.name === integrationSettings?.settings?.defaultSubtaskIssueTypeName,
        )?.id ?? '',
      enabled:
        !!issueTypesData &&
        !!integrationSettings?.settings?.defaultProjectKey &&
        !!integrationSettings?.settings?.defaultSubtaskIssueTypeName,
    })
  const [taskRequiredFieldsForm] = Form.useForm()
  const [subtaskRequiredFieldsForm] = Form.useForm()

  const [step, setStep] = useState<0 | 1>(0)

  const isLoadingRequiredFields = isLoadingTaskRequiredFields || isLoadingSubtaskRequiredFields
  const hasRequiredFields =
    (taskRequiredFields && taskRequiredFields.length > 0) ||
    (subtaskRequiredFields && subtaskRequiredFields.length > 0)

  const nonCompliantControls = useMemo(
    () => controlsData.filter((control) => isUnvalidatedControlStatus(control.status)),
    [controlsData],
  )

  const {
    data: jiraTicket,
    isPending: isGeneratingJiraTicketContent,
    mutateAsync: generateJiraTicketContentAsync,
    mutate: generateJiraTicketContent,
  } = useGenerateJiraTicketContent()
  const { showModal: showPreviewModal, renderModal: renderPreviewModal } = useJiraPreviewIssueModal(
    {
      issues: [
        {
          summary: jiraTicket?.parent.summary ?? '',
          description: jiraTicket?.parent.descriptionText ?? '',
        },
        ...(jiraTicket?.subtasks ?? []).map((issue) => ({
          summary: issue.summary,
          description: issue.descriptionText,
        })),
      ],
      isLoading: isGeneratingJiraTicketContent,
    },
  )

  const handleCreateTicketClick = useCallback(async () => {
    if (isCreatingIssue) {
      return
    }

    let taskRequiredFields: Record<string, unknown> = {}
    let subtaskRequiredFields: Record<string, unknown> = {}

    try {
      taskRequiredFields = await taskRequiredFieldsForm.validateFields()
      subtaskRequiredFields = await subtaskRequiredFieldsForm.validateFields()
    } catch (_err) {
      // Error is already handled by the form
      return
    }

    const jiraTicketWithSubtasks = await generateJiraTicketContentAsync({
      companyId,
      assessmentId,
      controlIds: selectedControlIds,
    })

    try {
      const request = buildIssueRequest({
        orgCompanyId: companyId,
        assessmentId: assessmentId,
        projectKey: integrationSettings?.settings?.defaultProjectKey ?? '',
        issueTypeName: integrationSettings?.settings?.defaultIssueTypeName ?? '',
        summary: jiraTicketWithSubtasks.parent.summary,
        descriptionAdf: jiraTicketWithSubtasks.parent.descriptionAdf,
        subtask: false,
        requiredFields: taskRequiredFields,
      })
      const response = await createJiraIssue(request)

      // Create subtasks in bulk (chunking handled by hook)
      if (jiraTicketWithSubtasks.subtasks.length > 0) {
        const subtaskRequests = jiraTicketWithSubtasks.subtasks.map((subtask) =>
          buildIssueRequest({
            orgCompanyId: companyId,
            assessmentId: assessmentId,
            projectKey: integrationSettings?.settings?.defaultProjectKey ?? '',
            issueTypeName: integrationSettings?.settings?.defaultSubtaskIssueTypeName ?? '',
            summary: subtask.summary,
            descriptionAdf: subtask.descriptionAdf,
            parentIssueKey: response.key,
            parentIssueId: response.id,
            subtask: true,
            requiredFields: subtaskRequiredFields,
          }),
        )

        const bulkResponse = await createJiraIssuesBulkWithChunking({
          issues: subtaskRequests,
        })

        // Log any errors but allow partial success
        if (bulkResponse.errors && bulkResponse.errors.length > 0) {
          console.error(
            `Failed to create ${bulkResponse.errors.length} out of ${subtaskRequests.length} subtasks:`,
            bulkResponse.errors,
          )
          // Show warning but continue if some succeeded
          if (bulkResponse.issues.length === 0) {
            // All failed - show error and return
            showErrorModal(JIRA_CREATE_ISSUE_MODAL_TEXT.ERROR_ALL_SUBTASKS_FAILED)
            return
          } else {
            // Partial success - show warning but continue
            showErrorModal(
              JIRA_CREATE_ISSUE_MODAL_TEXT.ERROR_PARTIAL_SUBTASKS_FAILED(
                bulkResponse.issues.length,
                subtaskRequests.length,
              ),
            )
            // Continue to close modal and call onFinish
          }
        }
      }

      trackIssueCreated({
        assessmentId: assessmentId,
        companyId: companyId,
        projectKey: integrationSettings?.settings?.defaultProjectKey ?? '',
        issueTypeName: integrationSettings?.settings?.defaultSubtaskIssueTypeName ?? '',
        controls: selectedControlIds.length,
        createdIssueKey: response.key,
        createdIssueLink: response.link,
      })
      onFinish(response)
    } catch (err) {
      if (isJiraIssueTooLargeError(err)) {
        console.error('Error creating Jira issue:', err)
        showErrorModal(JIRA_CREATE_ISSUE_MODAL_TEXT.ERROR_ISSUE_TOO_LARGE)
        return
      }
      if (isJiraNoPermissionError(err)) {
        console.error('Error creating Jira issue:', err)
        showErrorModal(JIRA_CREATE_ISSUE_MODAL_TEXT.ERROR_NO_PERMISSION)
        return
      }
      console.error('Error creating Jira issue:', err)
      showErrorModal(
        err instanceof Error ? err.message : JIRA_CREATE_ISSUE_MODAL_TEXT.ERROR_UNKNOWN,
      )
    }
  }, [
    assessmentId,
    companyId,
    createJiraIssue,
    createJiraIssuesBulkWithChunking,
    generateJiraTicketContentAsync,
    integrationSettings,
    isCreatingIssue,
    onFinish,
    selectedControlIds,
    showErrorModal,
    subtaskRequiredFieldsForm,
    taskRequiredFieldsForm,
    trackIssueCreated,
  ])

  const handlePreviewClick = useCallback(async () => {
    // Trigger generating Jira ticket content
    generateJiraTicketContent({
      companyId,
      assessmentId,
      controlIds: selectedControlIds,
    })
    // Show preview modal (it will display a loading state while generating the content)
    showPreviewModal()
  }, [assessmentId, companyId, generateJiraTicketContent, selectedControlIds, showPreviewModal])

  const isNothingSelected = selectedControlIds.length === 0
  const settings = integrationSettings?.settings

  const handlePrimaryClick = useCallback(async () => {
    if (step === 0 && hasRequiredFields) {
      setStep(1)
    } else {
      await handleCreateTicketClick()
    }
  }, [step, hasRequiredFields, handleCreateTicketClick])

  const handleSecondaryClick = useCallback(async () => {
    if (step === 1) {
      setStep(0)
    } else {
      await handlePreviewClick()
    }
  }, [step, handlePreviewClick])

  return (
    <Modal
      open
      onCancel={onCancel}
      title={JIRA_CREATE_ISSUE_MODAL_TEXT.TITLE}
      subtitle={JIRA_CREATE_ISSUE_MODAL_TEXT.SUBTITLE}
      confirmLoading={isLoadingIssueTypes || isLoadingRequiredFields || isLoadingControls}
      primaryButtonText={
        step === 0 && hasRequiredFields
          ? JIRA_CREATE_ISSUE_MODAL_TEXT.PRIMARY_BUTTON_NEXT
          : JIRA_CREATE_ISSUE_MODAL_TEXT.PRIMARY_BUTTON_CREATE
      }
      primaryButtonProps={{
        disabled: (isNothingSelected || isLoadingRequiredFields) && step === 0,
      }}
      showSecondaryButton
      secondaryButtonText={
        step === 1
          ? JIRA_CREATE_ISSUE_MODAL_TEXT.SECONDARY_BUTTON_BACK
          : JIRA_CREATE_ISSUE_MODAL_TEXT.SECONDARY_BUTTON_PREVIEW
      }
      secondaryButtonProps={{
        disabled: (isNothingSelected || isLoadingRequiredFields) && step === 0,
      }}
      onOk={handlePrimaryClick}
      onSecondary={handleSecondaryClick}
      className='flex w-2/3 min-w-[800px] max-w-screen-xl flex-col overflow-y-auto overflow-x-hidden' // Height set by tables
      hint={
        isNothingSelected
          ? undefined
          : JIRA_CREATE_ISSUE_MODAL_TEXT.HINT(
              settings?.defaultProjectKey ?? '',
              settings?.defaultIssueTypeName ?? '',
              selectedControlIds.length,
              settings?.defaultSubtaskIssueTypeName ?? '',
            )
      }
    >
      {renderPreviewModal()}
      {renderErrorModal()}
      {step === 0 ? (
        <div className='h-[65vh]'>
          <AgControlTable
            controls={nonCompliantControls || []}
            isLoading={isLoadingControls}
            readOnly={true}
            hideEvidenceRequest={true}
            filterContext='control_create_jira_issue'
            disableGroupBy={true}
            inModal={true}
            selection={{
              enabled: true,
              multiple: true,
              selectOnClick: true,
              initialSelectedIds: selectedControlIds,
              onSelectionChanged: (selectedIds) => setSelectedControlIds(selectedIds),
            }}
          />
        </div>
      ) : (
        <div className='h-[65vh] overflow-auto'>
          {settings?.defaultIssueTypeName &&
            taskRequiredFields &&
            taskRequiredFields.length > 0 && (
              <>
                <h2 className='mb-2 text-heading-medium'>
                  {JIRA_CREATE_ISSUE_MODAL_TEXT.REQUIRED_FIELDS_HEADING(
                    settings.defaultIssueTypeName,
                  )}
                </h2>
                <Form form={taskRequiredFieldsForm} className='w-1/3'>
                  {taskRequiredFields.map((requiredField) => (
                    <div key={requiredField.fieldId} className='mb-4'>
                      <label className='mb-2 flex items-center gap-1 text-main-foreground-primary text-label-default'>
                        {requiredField.name}
                      </label>
                      <Form.Item
                        name={requiredField.fieldId}
                        rules={[
                          {
                            required: true,
                            message: JIRA_CREATE_ISSUE_MODAL_TEXT.FIELD_REQUIRED_VALIDATION(
                              requiredField.name,
                            ),
                          },
                        ]}
                      >
                        <JiraFieldInput field={requiredField} />
                      </Form.Item>
                    </div>
                  ))}
                </Form>
              </>
            )}

          {settings?.defaultSubtaskIssueTypeName &&
            subtaskRequiredFields &&
            subtaskRequiredFields.length > 0 && (
              <>
                <h2 className='mb-2 text-heading-medium'>
                  {JIRA_CREATE_ISSUE_MODAL_TEXT.REQUIRED_FIELDS_HEADING(
                    settings.defaultSubtaskIssueTypeName,
                  )}
                </h2>
                <Form form={subtaskRequiredFieldsForm} className='w-1/3'>
                  {subtaskRequiredFields.map((requiredField) => (
                    <div key={requiredField.fieldId} className='mb-4'>
                      <label className='mb-2 flex items-center gap-1 text-main-foreground-primary text-label-default'>
                        {requiredField.name}
                      </label>
                      <Form.Item
                        name={requiredField.fieldId}
                        rules={[
                          {
                            required: true,
                            message: JIRA_CREATE_ISSUE_MODAL_TEXT.FIELD_REQUIRED_VALIDATION(
                              requiredField.name,
                            ),
                          },
                        ]}
                      >
                        <JiraFieldInput field={requiredField} />
                      </Form.Item>
                    </div>
                  ))}
                </Form>
              </>
            )}
        </div>
      )}
    </Modal>
  )
}

// Builds a CreateIssueRequest from the given parameters
// fields are explained here in detail https://developer.atlassian.com/server/jira/platform/jira-rest-api-examples/
function buildIssueRequest({
  orgCompanyId,
  assessmentId,
  projectKey,
  issueTypeName,
  summary,
  descriptionAdf,
  parentIssueKey,
  parentIssueId,
  subtask = false,
  requiredFields = {},
}: {
  orgCompanyId: string
  assessmentId: string
  projectKey: string
  issueTypeName: string
  summary: string
  descriptionAdf: unknown[]
  subtask: boolean
  parentIssueKey?: string
  parentIssueId?: string
  requiredFields?: Record<string, unknown>
}): CreateIssueRequest {
  return create(CreateIssueRequestSchema, {
    orgCompanyId,
    assessmentId,
    subtask: subtask,
    parentKey: parentIssueKey,
    fields: {
      project: {
        key: projectKey,
      },
      summary: summary,
      description: {
        type: 'doc',
        version: 1,
        content: descriptionAdf as JsonValue,
      },
      issuetype: {
        name: issueTypeName,
      },
      parent: parentIssueId // Prefer ID over key to avoid Jira quirks
        ? {
            id: parentIssueId,
          }
        : parentIssueKey
          ? {
              key: parentIssueKey,
            }
          : null,
      ...requiredFields,
    },
  })
}
